import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@cockpit/ui'
import { selectIsSuperAdmin, useAuthStore } from '@/stores/authStore'
import { navItems } from './nav'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Global jump palette (⌘K / Ctrl+K). Navigation only — no remote search.
 * Open state is owned by AppShell so the sidebar button can open it.
 */
export function CommandPalette({ open, onOpenChange }: Props) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin)

  const items = navItems.filter((item) => !item.superAdminOnly || isSuperAdmin)

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange])

  const go = useCallback(
    (to: string) => {
      onOpenChange(false)
      navigate(to)
    },
    [navigate, onOpenChange],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 overflow-hidden p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{t('command.title')}</DialogTitle>
        <Command className="rounded-lg border-0">
          <CommandInput placeholder={t('command.placeholder')} autoFocus />
          <CommandList>
            <CommandEmpty>{t('command.empty')}</CommandEmpty>
            <CommandGroup heading={t('command.pages')}>
              {items.map(({ to, labelKey, icon: Icon }) => (
                <CommandItem key={to} value={t(labelKey)} onSelect={() => go(to)}>
                  <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  {t(labelKey)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
